export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format a decimal value as a percentage string.
 * Handles both decimal (0.0949) and already-percentage (9.49) formats.
 * Values < 1 are assumed to be decimals and multiplied by 100.
 *
 * @param value - The value to format (decimal like 0.0949 or percentage like 9.49)
 * @param decimals - Number of decimal places (default 2)
 * @returns Formatted string like "9.49%"
 */
export const formatPercent = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) return 'N/A';
  // If value is < 1 and > -1, assume it's a decimal that needs *100
  // Exception: values between -1 and 1 that are already percentages (rare edge case)
  const displayValue = Math.abs(value) < 1 ? value * 100 : value;
  return `${displayValue.toFixed(decimals)}%`;
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatCurrencyCompact = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US').format(value);
};

export const getTierColor = (tier: string): string => {
  switch (tier) {
    case 'Tier1': return '#10b981';
    case 'Tier2': return '#f59e0b';
    case 'Tier3': return '#ef4444';
    default: return '#6b7280';
  }
};

export const getRatingColor = (rating: string | null | undefined): string => {
  if (!rating) return '#6b7280';
  switch (rating) {
    case 'Strong': return '#10b981';
    case 'Medium': return '#f59e0b';
    case 'Weak': return '#ef4444';
    default: return '#6b7280';
  }
};

export const getConfidenceColor = (confidence?: string): string => {
  switch (confidence) {
    case 'High': return '#10b981';
    case 'Medium': return '#f59e0b';
    case 'Low': return '#ef4444';
    default: return '#6b7280';
  }
};
